// Системное «назад» на Android: кнопка и жест от края экрана приходят одним
// событием @capacitor/app → backButton. Порядок как у нативных приложений:
//   1. открыт диалог / меню / выдвижная шторка — закрыть верхний слой;
//   2. есть история WebView — шаг назад по ней;
//   3. корневой экран — свернуть приложение (minimizeApp), а не закрыть:
//      так ведёт себя Android 12+ на корневых activity.
//
// Раньше слушатель жил в AppHeader.mounted: без снятия (ремаунт шапки удваивал
// шаг назад), на signin/signup шапки нет — там «назад» не работал вовсе,
// а на корне $router.go(-1) молча ничего не делал. Теперь ставится один раз
// в main.js, рядом с appUrlOpen.
//
// На iOS кнопки нет: там свайп от края — нативный жест WKWebView
// (allowsBackForwardNavigationGestures в ios/App/App/ViewController.swift).
//
// Верхний слой ищется по экземплярам Vuetify 2 через el.__vue__: у
// детачнутых диалогов и меню корневой элемент остаётся на месте (активатор),
// контент уезжает в [data-app]. У меню v-select свой VMenu с disableKeys —
// Escape его не закрывает, поэтому не эмулируем клавиатуру, а снимаем
// isActive (VSelect слушает input меню и синхронизирует isMenuActive).
// FT-073 (Vue 3 / Vuetify 4): __vue__ там нет — модуль переписать.

const DIALOG_SELECTOR = ".v-dialog__container";
const DIALOG_NAMES = ["v-dialog", "v-bottom-sheet"];
const MENU_SELECTOR = ".v-menu";
const MENU_NAMES = ["v-menu"];
const DRAWER_SELECTOR = ".v-navigation-drawer";
const DRAWER_NAMES = ["v-navigation-drawer"];

// Экземпляр Vuetify-компонента на корневом элементе. el.__vue__ у общего
// корня — тот, кто рендерился последним: у TopupDialog / PasskeyNudge /
// ConfirmDialog (v-dialog — их корень) это часто сама обёртка без isActive
// (замечено на живом Android). Поэтому спускаемся по _vnode.componentInstance,
// пока корень тот же, до компонента с нужным именем.
function vuetifyVm(el, names) {
  let vm = el && el.__vue__;
  while (vm) {
    if (names.includes(vm.$options.name)) return vm;
    const child = vm._vnode && vm._vnode.componentInstance;
    if (!child || child.$el !== el) return null;
    vm = child;
  }
  return null;
}

// Открытые в одном тике слои получают одинаковый activeZIndex; визуально
// сверху тот, чей контент (детачнутый в [data-app]) идёт в DOM позже.
function renderedLater(a, b) {
  const ca = a.$refs && a.$refs.content;
  const cb = b.$refs && b.$refs.content;
  if (!ca || !cb) return false;
  return Boolean(cb.compareDocumentPosition(ca) & Node.DOCUMENT_POSITION_FOLLOWING);
}

// Верхний открытый слой или null. Диалоги и меню сравниваются по
// activeZIndex (Stackable: меню внутри диалога всегда выше него); выдвижная
// шторка ниже любого из них, поэтому она — только если других слоёв нет.
// Постоянная (permanent / десктопная) шторка слоем не считается.
export function findTopOverlay(root = document) {
  let top = null;
  const consider = (el, names) => {
    const vm = vuetifyVm(el, names);
    if (!vm || !vm.isActive) return;
    const z = Number(vm.activeZIndex) || 0;
    if (!top || z > top.z || (z === top.z && renderedLater(vm, top.vm))) top = { vm, z };
  };
  root.querySelectorAll(DIALOG_SELECTOR).forEach((el) => consider(el, DIALOG_NAMES));
  root.querySelectorAll(MENU_SELECTOR).forEach((el) => consider(el, MENU_NAMES));
  if (top) return top.vm;

  let drawer = null;
  root.querySelectorAll(DRAWER_SELECTOR).forEach((el) => {
    const vm = vuetifyVm(el, DRAWER_NAMES);
    if (vm && vm.isActive && vm.reactsToClick) drawer = vm;
  });
  return drawer;
}

// Закрывает слой так же, как Escape: persistent-диалог не закрывается, а
// вздрагивает — в нём обычно форма, и «назад» не должен терять ввод.
export function closeOverlay(vm) {
  if (vm.persistent) {
    if (!vm.noClickAnimation && typeof vm.animateClick === "function") vm.animateClick();
    return;
  }
  vm.isActive = false;
}

export function handleBackButton(event, { router, app, root }) {
  const overlay = findTopOverlay(root);
  if (overlay) {
    closeOverlay(overlay);
    return "overlay";
  }
  if (event && event.canGoBack) {
    router.back();
    return "history";
  }
  app.minimizeApp();
  return "minimize";
}

// Подписка на всё время жизни приложения. Как только на backButton есть
// слушатель, Capacitor перестаёт сам делать webView.goBack() — вся логика здесь.
export function installHardwareBackHandler(capacitorApp, router, root = document) {
  return capacitorApp.addListener("backButton", (event) =>
    handleBackButton(event, { router, app: capacitorApp, root })
  );
}
